import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { getApiUrl } from '../client/lib/config';
import { DuplicateSuggestion, DuplicateDecision } from '../processing/interfaces';
import './StageC.css';

interface SynthesisData {
  term: string;
  synthesis: string;
  usage_count: number;
  pages: string;
}

interface StageCProps {
  suggestions: DuplicateSuggestion[];
  uniqueTerms: string[];
  onSubmit: (decisions: DuplicateDecision[]) => void;
  onScrollToTerm?: (termToFind: string) => void;
  onDecisionsChange?: (decisions: DuplicateDecision[]) => void;
  projectId?: string;
  stage: 'C1' | 'C2' | 'C3'; // Updated to include C3
  isLoading?: boolean; // New prop to indicate when ANN process is still running
}

// Simple error boundary component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('StageC Error Boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="stage-c container">
          <div className="error-message">
            <h3>Something went wrong</h3>
            <p>There was an error displaying the duplicate detection interface. Please try regenerating suggestions.</p>
            <button 
              className="button secondary mt-2" 
              onClick={() => this.setState({ hasError: false })}
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const StageC: React.FC<StageCProps> = ({ 
  suggestions, 
  uniqueTerms, 
  onSubmit, 
  onScrollToTerm, 
  onDecisionsChange, 
  projectId, 
  stage, 
  isLoading 
}) => {
  const [decisions, setDecisions] = useState<DuplicateDecision[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error' | 'queued'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [editedMainTerms, setEditedMainTerms] = useState<Record<number, boolean>>({});
  const [decisionsLoaded, setDecisionsLoaded] = useState(false);
  // Track original main terms when editing starts
  const [originalMainTerms, setOriginalMainTerms] = useState<Record<number, string>>({});

  // New state for synthesis data and tooltips
  const [synthesisData, setSynthesisData] = useState<SynthesisData[]>([]);
  const [hoveredTerm, setHoveredTerm] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
  const [lastSaveTime, setLastSaveTime] = useState<Date | null>(null);
  const [isTooltipHovered, setIsTooltipHovered] = useState(false);
  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sectionsPerPage = 5;

  // State for C1 decisions display
  const [c1Decisions, setC1Decisions] = useState<any[]>([]);
  const [c1CrossReferences, setC1CrossReferences] = useState<{[key: string]: string[]}>({});
  const [c1Subentries, setC1Subentries] = useState<{[key: string]: string}>({});
  const [c1MergedTerms, setC1MergedTerms] = useState<{[key: string]: string}>({});
  const [isMergedExpanded, setIsMergedExpanded] = useState<boolean>(false);
  const [isSubentriesExpanded, setIsSubentriesExpanded] = useState<boolean>(false);
  const [isCrossRefsExpanded, setIsCrossRefsExpanded] = useState<boolean>(false);
  
  // Pagination states
  const [mergedPage, setMergedPage] = useState<number>(1);
  const [subentriesPage, setSubentriesPage] = useState<number>(1);
  const [crossRefsPage, setCrossRefsPage] = useState<number>(1);
  
  const ITEMS_PER_PAGE = 10;

  // Grouping helpers: consolidate by dominant/main term
  const mergedGroups = useMemo(() => {
    const groups = new Map<string, string[]>();
    Object.entries(c1MergedTerms).forEach(([merged, main]) => {
      const arr = groups.get(main) || [];
      arr.push(merged);
      groups.set(main, arr);
    });
    return Array.from(groups.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [c1MergedTerms]);

  const subentryGroups = useMemo(() => {
    const groups = new Map<string, string[]>();
    Object.entries(c1Subentries).forEach(([sub, parent]) => {
      const arr = groups.get(parent) || [];
      arr.push(sub);
      groups.set(parent, arr);
    });
    return Array.from(groups.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [c1Subentries]);

  // Pagination helpers
  const getPaginatedItems = (items: any[], page: number) => {
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return items.slice(startIndex, endIndex);
  };

  const getTotalPages = (itemCount: number) => Math.ceil(itemCount / ITEMS_PER_PAGE);

  const renderPaginationControls = (currentPage: number, totalPages: number, onPageChange: (page: number) => void) => {
    if (totalPages <= 1) return null;
    
    return (
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/30">
        <span className="text-xs text-muted-foreground">
          Page {currentPage} of {totalPages}
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="px-2 py-1 text-xs bg-card border border-border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted/30 text-foreground"
          >
            Previous
          </button>
          <button
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="px-2 py-1 text-xs bg-card border border-border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted/30 text-foreground"
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  // Auto-save states (optimized to prevent re-renders)
  const isSavingRef = useRef(false);
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const lastSavedDecisionsRef = useRef<string>('');
  // Track whether the user has interacted in this session to avoid auto-saving LLM suggestions
  const hasUserInteractedRef = useRef<boolean>(false);

  // Refs
  const stageCContainerRef = useRef<HTMLDivElement>(null);

  // Fetch synthesis data when component mounts
  useEffect(() => {
    const fetchSynthesisData = async () => {
      if (!projectId) return;

      try {
        const response = await makeAuthenticatedRequest(getApiUrl(`/api/projects/${projectId}/synthesis-data`));
        if (response.ok) {
          const data = await response.json();
          setSynthesisData(data.synthesisData || []);
          console.log(`[StageC] Loaded ${data.synthesisData?.length || 0} synthesis terms`);
        } else {
          console.warn('[StageC] Failed to fetch synthesis data:', response.statusText);
        }
      } catch (error) {
        console.warn('[StageC] Error fetching synthesis data:', error);
      }
    };

    fetchSynthesisData();
  }, [projectId]);

  // Fetch completed stage relationships for display in subsequent stages
  useEffect(() => {
    if ((stage === 'C2' || stage === 'C3') && projectId) {
      const fetchCompletedRelationships = async () => {
        try {
          // For C2 and C3, only show C1 completed results
          // This should only fetch relationships from completed C1 stage, not current decisions
          const response = await makeAuthenticatedRequest(getApiUrl(`/api/projects/${projectId}/completed-relationships`));
          
          if (response.ok) {
            const data = await response.json();
            setC1Decisions([]); // We don't need the raw decisions for display
            setC1CrossReferences(data.crossReferences || {});
            setC1Subentries(data.subentries || {});
            setC1MergedTerms(data.mergedTerms || {});
            console.log(`[StageC] Loaded completed relationships for display in ${stage}:`, {
              crossReferences: Object.keys(data.crossReferences || {}).length,
              subentries: Object.keys(data.subentries || {}).length,
              mergedTerms: Object.keys(data.mergedTerms || {}).length,
              debug: data.debug
            });
          } else {
            console.error('[StageC] Failed to fetch completed relationships:', response.statusText);
            // Fallback to empty state if no completed relationships exist yet
            setC1CrossReferences({});
            setC1Subentries({});
            setC1MergedTerms({});
          }
        } catch (error) {
          console.error('[StageC] Error fetching completed relationships:', error);
          // Fallback to empty state on error
          setC1CrossReferences({});
          setC1Subentries({});
          setC1MergedTerms({});
        }
      };
      
      fetchCompletedRelationships();
    }
  }, [projectId, stage]);

  // Helper: terms locked as prior subentries (cannot be merged or cross-referenced in later stages)
  const isLockedSubentry = useCallback((term: string) => {
    return (stage === 'C2' || stage === 'C3') && !!c1Subentries[term];
  }, [stage, c1Subentries]);

  // Generate unique storage key for this project and stage
  const getLocalStorageKey = () => {
    return `stagec_decisions_${projectId}_${stage}_v2`; // Added v2 to avoid conflicts with old data
  };

  // Enhanced tooltip helper functions that handle merged terms
  const getSynthesisForTerm = (term: string): SynthesisData | null => {
    // The synthesis data from the API should already have merged page numbers
    // thanks to the processing in filterSynthesisForC2
    return synthesisData.find(item => item.term === term) || null;
  };

  const calculateTooltipPosition = (mouseX: number, mouseY: number) => {
    const isMobile = window.innerWidth <= 768;
    const tooltipWidth = isMobile ? Math.min(window.innerWidth - 30, 280) : 320;
    const tooltipHeight = isMobile ? 250 : 150;
    const margin = isMobile ? 10 : 15;
    const offset = isMobile ? 5 : 10;

    let x = mouseX + offset;
    let y = mouseY - offset;

    // Adjust horizontal position if tooltip would go off-screen
    if (x + tooltipWidth > window.innerWidth - margin) {
      x = mouseX - tooltipWidth - offset; // Show on left side of cursor
    }
    if (x < margin) {
      x = margin; // Ensure minimum margin from left edge
    }

    // Adjust vertical position if tooltip would go off-screen
    if (y + tooltipHeight > window.innerHeight - margin) {
      y = mouseY - tooltipHeight - offset; // Show above cursor
    }
    if (y < margin) {
      y = margin; // Ensure minimum margin from top edge
    }

    // On mobile, center horizontally if there's space
    if (isMobile && window.innerWidth > tooltipWidth + 2 * margin) {
      x = Math.max(margin, Math.min(window.innerWidth - tooltipWidth - margin, mouseX - tooltipWidth / 2));
    }

    return { x, y };
  };

  const handleTermMouseEnter = (term: string, event: React.MouseEvent) => {
    // Clear any pending hide timeout
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }

    const synthesis = getSynthesisForTerm(term);
    if (synthesis && synthesis.synthesis) {
      setHoveredTerm(term);
      const position = calculateTooltipPosition(event.clientX, event.clientY);
      setTooltipPosition(position);
    }
  };

  const handleTermMouseLeave = () => {
    // Use a delay before hiding to allow mouse to move to tooltip
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }
    
    tooltipTimeoutRef.current = setTimeout(() => {
      if (!isTooltipHovered) {
        setHoveredTerm(null);
        setTooltipPosition(null);
      }
    }, 150); // 150ms delay to allow mouse movement to tooltip
  };

  const handleTooltipMouseEnter = () => {
    // Clear hide timeout when mouse enters tooltip
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
    setIsTooltipHovered(true);
  };

  const handleTooltipMouseLeave = () => {
    setIsTooltipHovered(false);
    setHoveredTerm(null);
    setTooltipPosition(null);
  };

  const handleTermMouseMove = (event: React.MouseEvent) => {
    if (hoveredTerm) {
      const position = calculateTooltipPosition(event.clientX, event.clientY);
      setTooltipPosition(position);
    }
  };

  // Save to localStorage as backup
  const saveToLocalStorage = useCallback((decisionsToSave: DuplicateDecision[]) => {
    try {
      const storageData = {
        decisions: decisionsToSave,
        timestamp: Date.now(),
        projectId,
        stage,
        version: '1.0'
      };
      localStorage.setItem(getLocalStorageKey(), JSON.stringify(storageData));
      console.log('[StageC] Saved to localStorage as backup');
    } catch (error) {
      console.warn('[StageC] Failed to save to localStorage:', error);
    }
  }, [projectId, stage]);

  // Load from localStorage
  const loadFromLocalStorage = useCallback((): DuplicateDecision[] | null => {
    try {
      const stored = localStorage.getItem(getLocalStorageKey());
      if (stored) {
        const parsedData = JSON.parse(stored);
        if (parsedData.projectId === projectId && parsedData.stage === stage && parsedData.decisions) {
          console.log('[StageC] Loaded decisions from localStorage backup');
          return parsedData.decisions;
        }
      }
    } catch (error) {
      console.warn('[StageC] Failed to load from localStorage:', error);
    }
    return null;
  }, [projectId, stage]);

  // Clear localStorage when no longer needed
  const clearLocalStorage = useCallback(() => {
    try {
      localStorage.removeItem(getLocalStorageKey());
      console.log('[StageC] Cleared localStorage backup');
    } catch (error) {
      console.warn('[StageC] Failed to clear localStorage:', error);
    }
  }, [projectId, stage]);

  // Auth helper function
  const makeAuthenticatedRequest = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('orca_auth_token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    return fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers
      }
    });
  };

  // Enhanced immediate save function with retry logic and visual feedback
  const immediateServerSave = useCallback(async (decisionsToSave: DuplicateDecision[]) => {
    if (!projectId || isSavingRef.current) {
      console.log(`[StageC-${stage}] Skipping save - projectId: ${!!projectId}, isSaving: ${isSavingRef.current}`);
      return false;
    }

    console.log(`[StageC-${stage}] Starting immediate save with ${decisionsToSave.length} decisions`);
    
    // Set saving status
    isSavingRef.current = true;

    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();
    
    const maxRetries = 3;
    let attempt = 0;

    while (attempt < maxRetries) {
      try {
        console.log(`[StageC-${stage}] Save attempt ${attempt + 1}/${maxRetries}`);
        const response = await makeAuthenticatedRequest(getApiUrl(`/api/projects/${projectId}/save-stage-c-decisions`), {
          method: 'POST',
          body: JSON.stringify({
            stage: stage,
            decisions: decisionsToSave,
            // Provide any user-created main terms (not present among original candidates) for better downstream handling
            createdMainTerms: decisionsToSave
              .map((d, idx) => {
                const s = suggestions[idx];
                const candidates = s ? [s.suggested_main, ...(s.terms || [])] : [];
                return (d.main_term && !candidates.includes(d.main_term)) ? d.main_term : null;
              })
              .filter((t): t is string => !!t)
          }),
          signal: abortControllerRef.current?.signal
        });

        if (!response.ok) {
          throw new Error(`Server save failed: ${response.status} - ${response.statusText}`);
        }

        // Success!
        const currentDecisionsString = JSON.stringify(decisionsToSave);
        lastSavedDecisionsRef.current = currentDecisionsString;
        setLastSaveTime(new Date());
        
        console.log(`[StageC-${stage}] ✅ Server save successful on attempt ${attempt + 1} - ${decisionsToSave.length} decisions saved`);
        
        // Clear localStorage backup since server save succeeded
        clearLocalStorage();
        
        isSavingRef.current = false;
        abortControllerRef.current = null;
        return true;

      } catch (error) {
        attempt++;
        console.warn(`[StageC-${stage}] ❌ Save attempt ${attempt} failed:`, error);
        
        if (error instanceof Error && error.name === 'AbortError') {
          console.log(`[StageC-${stage}] Save request was aborted`);
          break; // Don't retry aborted requests
        }
        
        if (attempt >= maxRetries) {
          console.error(`[StageC-${stage}] 🚨 Server save failed after ${maxRetries} attempts`);
          
          // Keep localStorage backup since server save failed
          saveToLocalStorage(decisionsToSave);
          console.log(`[StageC-${stage}] 💾 Kept localStorage backup due to server save failure`);
          break;
        }
        
        // Wait before retry (exponential backoff)
        const backoffTime = Math.pow(2, attempt) * 1000;
        console.log(`[StageC-${stage}] Waiting ${backoffTime}ms before retry...`);
        await new Promise(resolve => setTimeout(resolve, backoffTime));
      }
    }
    
    isSavingRef.current = false;
    abortControllerRef.current = null;
    return false;
  }, [projectId, stage, clearLocalStorage, saveToLocalStorage, suggestions]);

  // Legacy auto-save function for compatibility - now calls immediate save
  const autoSave = useCallback(async () => {
    if (decisions.length === 0) return;
    await immediateServerSave(decisions);
  }, [decisions, immediateServerSave]);

  // Load existing decisions on mount
  useEffect(() => {
    const loadExistingDecisions = async () => {
      if (!projectId) return;

      console.log(`[StageC] Loading existing decisions for ${stage}...`);
      let serverDecisions: DuplicateDecision[] | null = null;
      let localDecisions: DuplicateDecision[] | null = null;

      // Try to load from server first
      try {
        const response = await makeAuthenticatedRequest(getApiUrl(`/api/projects/${projectId}/stage-c-decisions/${stage}`));

        if (response.ok) {
          const data = await response.json();
          if (data.decisions && data.decisions.length > 0) {
            serverDecisions = data.decisions;
            console.log(`[StageC] Loaded ${data.decisions.length} decisions from server`);
          }
        }
      } catch (error) {
        console.warn('[StageC] Failed to load from server:', error);
      }

      // Try to load from localStorage as backup
      localDecisions = loadFromLocalStorage();

      // Conflict resolution logic
      if (serverDecisions && localDecisions) {
        // Compare timestamps or decision counts to determine which is more recent/complete
        const serverString = JSON.stringify(serverDecisions);
        const localString = JSON.stringify(localDecisions);
        
        if (serverString !== localString) {
          console.log('[StageC] Conflict detected between server and local decisions');
          
          // For now, prefer server decisions but keep local as backup
          // In the future, could implement sophisticated merge logic
          console.log('[StageC] Using server decisions, keeping local as backup');
          setDecisions(serverDecisions);
          lastSavedDecisionsRef.current = serverString;
        } else {
          console.log('[StageC] Server and local decisions match');
          setDecisions(serverDecisions);
          lastSavedDecisionsRef.current = serverString;
          clearLocalStorage(); // Clean up since they match
        }
      } else if (serverDecisions) {
        console.log('[StageC] Using server decisions');
        setDecisions(serverDecisions);
        lastSavedDecisionsRef.current = JSON.stringify(serverDecisions);
        clearLocalStorage(); // Clean up local storage
        if (onDecisionsChange) {
          onDecisionsChange(serverDecisions);
        }
      } else if (localDecisions) {
        console.log('[StageC] Using local backup decisions');
        setDecisions(localDecisions);
        if (onDecisionsChange) {
          onDecisionsChange(localDecisions);
        }
        
        // Try to save local decisions to server
        immediateServerSave(localDecisions);
      } else {
        console.log('[StageC] No existing decisions found');
      }
      
      setDecisionsLoaded(true);
    };

    loadExistingDecisions();
  }, [projectId, stage, loadFromLocalStorage, clearLocalStorage, immediateServerSave, onDecisionsChange]);

  // Cleanup auto-save timeout and abort controller on unmount
  useEffect(() => {
    return () => {
      // Immediate save before unmounting if there are unsaved changes
      if (decisions.length > 0 && !isSavingRef.current) {
        const currentDecisionsString = JSON.stringify(decisions);
        if (currentDecisionsString !== lastSavedDecisionsRef.current) {
          console.log(`[StageC] Component unmounting - performing immediate save for ${stage}`);
          // Immediate save without debouncing
          autoSave();
        }
      }
      
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, [decisions, stage, autoSave]);

  // Initialize decisions based on suggestions - only if no decisions were loaded
  useEffect(() => {
    // Only initialize if decisions haven't been loaded AND we have suggestions
    if (suggestions.length > 0 && decisionsLoaded && decisions.length === 0) {
      console.log(`[StageC-${stage}] Initializing new decisions with ${suggestions.length} suggestions`);
      const newDecisions = suggestions.map((suggestion: any, index) => {
        // Prefer LLM preselected, else fall back to suggested_main/first term
        const pre = suggestion.preselected as { main_term?: string; relationships?: Record<string,string> } | undefined;
        const mainTerm = pre?.main_term || suggestion.suggested_main || suggestion.terms[0];
        const relationships: Record<string, string> = {};
        
        // Start with keep_separate, then overlay LLM relationships if present
        suggestion.terms.forEach((term: string) => {
          if (term !== mainTerm) {
            relationships[term] = isLockedSubentry(term) ? 'keep_separate' : 'keep_separate';
          }
        });
        if (pre?.relationships) {
          for (const [term, rel] of Object.entries(pre.relationships)) {
            if (term !== mainTerm && suggestion.terms.includes(term)) {
              relationships[term] = rel as string;
            }
          }
        }

        return {
          action: 'merge',
          main_term: mainTerm,
          original_terms: suggestion.terms,
          relationships: relationships
        } as DuplicateDecision;
      });

      console.log(`[StageC-${stage}] Initialized ${newDecisions.length} decisions (pre-populated where available)`);
      setDecisions(newDecisions);
      if (onDecisionsChange) {
        onDecisionsChange(newDecisions);
      }
      
      // Remove immediate auto-save here so user can review before persisting
      // setTimeout(() => {
      //   immediateServerSave(newDecisions);
      // }, 100);
    }
  }, [suggestions, decisionsLoaded, decisions.length, stage, immediateServerSave, onDecisionsChange, isLockedSubentry]);

  // Auto-save decisions with improved reliability
  const pendingSaveRef = useRef<DuplicateDecision[] | null>(null);
  const SAVE_DEBOUNCE = 1000; // Increased to 1 second for better stability

  useEffect(() => {
    if (decisions.length > 0 && hasUserInteractedRef.current) {
      const currentDecisionsString = JSON.stringify(decisions);
      
      // Clear any pending save
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }

      // Queue new save with longer debounce
      setSaveStatus('queued');
      autoSaveTimeoutRef.current = setTimeout(async () => {
        // If already saving, queue this version
        if (isSavingRef.current) {
          pendingSaveRef.current = decisions;
          setSaveStatus('queued');
          return;
        }

        // Only save if decisions have actually changed
        if (currentDecisionsString !== lastSavedDecisionsRef.current) {
          try {
            isSavingRef.current = true;
            setSaveStatus('saving');
            setSaveError(null);
            
            await immediateServerSave(decisions);
            lastSavedDecisionsRef.current = currentDecisionsString;
            
            // Call parent callback after successful save
            if (onDecisionsChange) {
              onDecisionsChange(decisions);
            }

            setSaveStatus('saved');
            setTimeout(() => setSaveStatus('idle'), 2000); // Clear 'saved' after 2s

            // Check for pending save after success
            if (pendingSaveRef.current) {
              const pendingDecisions = pendingSaveRef.current;
              pendingSaveRef.current = null;
              setDecisions(pendingDecisions); // Trigger save of pending changes
            }
          } catch (error) {
            console.error('Failed to save decisions:', error);
            setSaveStatus('error');
            setSaveError(error instanceof Error ? error.message : 'Failed to save changes');
          } finally {
            isSavingRef.current = false;
          }
        }
      }, SAVE_DEBOUNCE);
    }
  }, [decisions, immediateServerSave]);

  const handleRelationshipChange = (
    decisionIndex: number, 
    term: string, 
    relationship: 'merge' | 'keep_separate' | 'subentry' | 'cross_reference'
  ) => {
    if (isLockedSubentry(term)) {
      console.log(`[StageC-${stage}] Ignoring change for locked subentry term: ${term}`);
      return;
    }
    console.log(`[StageC-${stage}] ✨ USER ACTION: Relationship change for "${term}" -> ${relationship}`);
    hasUserInteractedRef.current = true;
    
    setDecisions(prev => {
      const newDecisions = [...prev];
      const decision = { ...newDecisions[decisionIndex] };
      
      if (!decision.relationships) {
        decision.relationships = {};
      }
      
      decision.relationships = { ...decision.relationships };
      decision.relationships[term] = relationship;
      
      newDecisions[decisionIndex] = decision;
      
      // ENHANCED DEBUGGING: Log the complete decision structure when user makes a merge
      if (relationship === 'merge') {
        console.log(`[StageC-${stage}] 🔥 MERGE DECISION CREATED:`);
        console.log(`[StageC-${stage}]   Main term: "${decision.main_term}"`);
        console.log(`[StageC-${stage}]   Term being merged: "${term}"`);
        console.log(`[StageC-${stage}]   Full decision:`, JSON.stringify(decision, null, 2));
        
        // Count current merges in this decision
        const mergeCount = Object.values(decision.relationships).filter(r => r === 'merge').length;
        console.log(`[StageC-${stage}]   Total merges in this group: ${mergeCount}`);
      }
      
      // Log overall merge statistics
      const allMerges = newDecisions.flatMap(d => 
        Object.entries(d.relationships || {}).filter(([_, rel]) => rel === 'merge')
      );
      console.log(`[StageC-${stage}] 📊 TOTAL USER MERGES: ${allMerges.length} across all decisions`);
      
      // Immediately save to localStorage as backup
      saveToLocalStorage(newDecisions);
      
      // Trigger immediate server save
      immediateServerSave(newDecisions);
      
      return newDecisions;
    });
  };

  const handleMainTermChange = (decisionIndex: number, newMainTerm: string) => {
    console.log(`[StageC] Main term change: ${newMainTerm}`);
    hasUserInteractedRef.current = true;
    
    setDecisions(prev => {
      const newDecisions = [...prev];
      const oldDecision = newDecisions[decisionIndex];
      
      // CRITICAL FIX: Remove the new main term from its own relationships to prevent self-references
      const cleanedRelationships = { ...oldDecision.relationships };
      if (cleanedRelationships && cleanedRelationships.hasOwnProperty(newMainTerm)) {
        console.log(`[StageC] 🚫 REMOVED SELF-REFERENCE: "${newMainTerm}" cannot have relationship with itself`);
        delete cleanedRelationships[newMainTerm];
      }
      
      // Update the main term with cleaned relationships
      newDecisions[decisionIndex] = {
        ...oldDecision,
        main_term: newMainTerm,
        relationships: cleanedRelationships
      };
      
      // Immediately save to localStorage as backup
      saveToLocalStorage(newDecisions);
      
      // Trigger immediate server save
      immediateServerSave(newDecisions);
      
      return newDecisions;
    });
  };

  const handleMainTermEditFinished = (decisionIndex: number) => {
    const originalTerm = originalMainTerms[decisionIndex];
    const currentDecision = decisions[decisionIndex];
    const currentMainTerm = currentDecision?.main_term;
    const originalSuggestion = suggestions[decisionIndex];
    
    // ENHANCED: Only add the original term as a related term if it was part of the original suggestion
    // This prevents custom user-created terms from being incorrectly added to the multiple choice options
    if (originalTerm && currentMainTerm && originalTerm !== currentMainTerm) {
      // Check if the original term was actually part of the original suggestion terms
      const wasOriginalTerm = originalSuggestion?.terms.includes(originalTerm);
      
      if (wasOriginalTerm) {
        console.log(`[StageC] Editing finished: preserving original term "${originalTerm}" as related term (was part of original suggestion)`);
        
        setDecisions(prev => {
          const newDecisions = [...prev];
          const decision = { ...newDecisions[decisionIndex] };
          
          if (!decision.relationships) {
            decision.relationships = {};
          }
          
          // Only add the original term if it's not already in relationships
          if (!decision.relationships[originalTerm]) {
            decision.relationships = { ...decision.relationships };
            decision.relationships[originalTerm] = 'keep_separate';
            console.log(`[StageC] Added original term "${originalTerm}" as "keep_separate" relationship`);
          }
          
          newDecisions[decisionIndex] = decision;
          
          // Save the updated decisions
          saveToLocalStorage(newDecisions);
          immediateServerSave(newDecisions);
          
          return newDecisions;
        });
      } else {
        console.log(`[StageC] Editing finished: discarding custom term "${originalTerm}" (was not part of original suggestion)`);
      }
    }
    
    // Clear the original term tracking and exit edit mode
    setOriginalMainTerms(prev => {
      const updated = { ...prev };
      delete updated[decisionIndex];
      return updated;
    });
    
    toggleEditMainTerm(decisionIndex);
  };

  const toggleEditMainTerm = (decisionIndex: number) => {
    setEditedMainTerms(prev => {
      const isCurrentlyEditing = prev[decisionIndex];
      const newState = {
        ...prev,
        [decisionIndex]: !isCurrentlyEditing
      };
      
      // If starting to edit, store the original main term
      if (!isCurrentlyEditing) {
        const currentDecision = decisions[decisionIndex];
        if (currentDecision?.main_term) {
          setOriginalMainTerms(prevOriginal => ({
            ...prevOriginal,
            [decisionIndex]: currentDecision.main_term!
          }));
          console.log(`[StageC] Started editing, stored original term: "${currentDecision.main_term}"`);
        }
      }
      
      return newState;
    });
  };

  const scrollToTerm = (termToFind: string) => {
    console.log(`[StageC] Attempting to scroll to term: "${termToFind}"`);
    
    // Always use the provided onScrollToTerm function if available
    // This should scroll to the CSV table, not highlight terms in this component
    if (onScrollToTerm) {
      console.log(`[StageC] Using provided onScrollToTerm function to scroll to CSV table`);
      onScrollToTerm(termToFind);
      return;
    }

    // Enhanced fallback - specifically target CSV table area, not this component
    console.log(`[StageC] Using fallback scroll implementation - targeting CSV table only`);
    
    // Look specifically for the CSV table container using the actual class from ProjectDetail.tsx
    const csvContainer = document.querySelector('.scroll-smooth') || document.querySelector('table');
    
    if (csvContainer) {
      // Search within the CSV container only
      const termElements = csvContainer.querySelectorAll('[data-term]');
      console.log(`[StageC] Found ${termElements.length} elements with data-term in CSV container`);
      
      let targetElement: Element | null = null;
      
      // Look for exact match first in CSV area
      for (const element of termElements) {
        const elementTerm = element.getAttribute('data-term');
        if (elementTerm === termToFind) {
          targetElement = element;
          console.log(`[StageC] Found exact match in CSV for "${termToFind}"`);
          break;
        }
      }
      
      // If no exact match, try partial match in CSV area
      if (!targetElement) {
        for (const element of termElements) {
          const elementTerm = element.getAttribute('data-term');
          if (elementTerm && elementTerm.toLowerCase().includes(termToFind.toLowerCase())) {
            targetElement = element;
            console.log(`[StageC] Found partial match in CSV for "${termToFind}": "${elementTerm}"`);
            break;
          }
        }
      }
      
      // Also try searching in table cells within CSV container
      if (!targetElement) {
        const tableCells = csvContainer.querySelectorAll('td[data-term-cell="true"]');
        console.log(`[StageC] Searching in ${tableCells.length} table cells within CSV container`);
        
        for (const cell of tableCells) {
          if (cell.textContent && cell.textContent.toLowerCase().includes(termToFind.toLowerCase())) {
            targetElement = cell;
            console.log(`[StageC] Found term in CSV table cell: "${cell.textContent.substring(0, 50)}..."`);
            break;
          }
        }
      }

      if (targetElement) {
        console.log(`[StageC] Scrolling to target element in CSV table`);
        
        // Scroll the CSV container, not the document
        const scrollContainer = document.querySelector('.scroll-smooth');
        if (scrollContainer && targetElement) {
          // Calculate the position relative to the scroll container
          const containerRect = scrollContainer.getBoundingClientRect();
          const targetRect = targetElement.getBoundingClientRect();
          const scrollTop = scrollContainer.scrollTop;
          const relativeTop = targetRect.top - containerRect.top + scrollTop;
          
          // Scroll to the calculated position
          scrollContainer.scrollTo({
            top: relativeTop - scrollContainer.clientHeight / 3, // Center the element with some offset
            behavior: 'smooth'
          });
        } else {
          // Fallback to regular scroll
          targetElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }
        
        // Add bump effect instead of highlight
        targetElement.classList.add('bump-effect');
        setTimeout(() => {
          targetElement?.classList.remove('bump-effect');
        }, 600);
      } else {
        console.warn(`[StageC] Could not find term "${termToFind}" in CSV table`);
      }
    } else {
      console.warn(`[StageC] Could not find CSV container to search for term "${termToFind}"`);
    }
  };

  const handleSubmit = async () => {
    // ENHANCED DEBUGGING: Log comprehensive submission details
    console.log(`[StageC-${stage}] 🚀 SUBMISSION STARTING`);
    console.log(`[StageC-${stage}]   Total decisions: ${decisions.length}`);
    
    // Analyze the decisions being submitted
    const mergeStats = {
      totalMerges: 0,
      decisionsWithMerges: 0,
      mergesByDecision: [] as { index: number; mainTerm: string; mergedTerms: string[] }[]
    };
    
    decisions.forEach((decision, index) => {
      if (decision.relationships) {
        const merges = Object.entries(decision.relationships).filter(([_, rel]) => rel === 'merge');
        if (merges.length > 0) {
          mergeStats.totalMerges += merges.length;
          mergeStats.decisionsWithMerges++;
          mergeStats.mergesByDecision.push({
            index,
            mainTerm: decision.main_term || 'UNKNOWN',
            mergedTerms: merges.map(([term]) => term)
          });
        }
      }
    });
    
    console.log(`[StageC-${stage}] 📊 MERGE ANALYSIS BEFORE SUBMISSION:`);
    console.log(`[StageC-${stage}]   Total merge decisions: ${mergeStats.totalMerges}`);
    console.log(`[StageC-${stage}]   Decisions with merges: ${mergeStats.decisionsWithMerges}/${decisions.length}`);
    
    mergeStats.mergesByDecision.forEach(merge => {
      console.log(`[StageC-${stage}]   Decision ${merge.index}: "${merge.mainTerm}" ← [${merge.mergedTerms.join(', ')}]`);
    });
    
    console.log(`[StageC-${stage}] 📤 COMPLETE SUBMISSION DATA:`, JSON.stringify(decisions, null, 2));
    
    // VALIDATE USER DECISIONS for conflicts
    try {
      const { validateUserDecisions } = await import('../processing/llmDecisionValidator.js');
      const validationResult = validateUserDecisions(decisions);
      
      if (!validationResult.isValid) {
        console.error(`[StageC-${stage}] ❌ USER DECISION CONFLICTS DETECTED:`);
        validationResult.conflicts.forEach(conflict => console.error(`  - ${conflict}`));
        setErrorMessage(
          `Decision conflicts detected:\n${validationResult.conflicts.slice(0, 3).join('\n')}` +
          (validationResult.conflicts.length > 3 ? `\n... and ${validationResult.conflicts.length - 3} more conflicts` : '')
        );
        return;
      }
      
      console.log(`[StageC-${stage}] ✅ USER DECISIONS VALIDATED - No conflicts found`);
    } catch (error) {
      console.warn(`[StageC-${stage}] ⚠️ Decision validation failed, proceeding anyway:`, error);
    }
    
    // Basic validation for required fields
    const invalidDecisions = decisions.filter((decision, index) => {
      if (!decision.main_term) {
        return true;
      }
      return false;
    });

    if (invalidDecisions.length > 0) {
      console.error(`[StageC-${stage}] ❌ BASIC VALIDATION FAILED: ${invalidDecisions.length} invalid decisions`);
      setErrorMessage('Please ensure all suggestions have a main term selected.');
      return;
    }

    console.log(`[StageC-${stage}] ✅ ALL VALIDATION PASSED - Calling onSubmit with decisions`);
    setErrorMessage(null);
    onSubmit(decisions);
  };

  // Calculate pagination - moved up to be available for callbacks
  const totalPages = Math.ceil(suggestions.length / sectionsPerPage);
  const startIndex = (currentPage - 1) * sectionsPerPage;
  const endIndex = Math.min(startIndex + sectionsPerPage, suggestions.length);
  const currentSuggestions = suggestions.slice(startIndex, endIndex);

  return (
    <ErrorBoundary>
      <div ref={stageCContainerRef} className="stage-c container">
        <div className="flex justify-between items-center mb-4 px-4 pt-4">
          <h2 className="text-xl font-semibold">Stage {stage} - Duplicate Detection</h2>
          <div className="flex items-center gap-2">
            {saveStatus === 'queued' && (
              <span className="text-sm text-muted-foreground">Changes queued...</span>
            )}
            {saveStatus === 'saving' && (
              <span className="text-sm text-muted-foreground">Saving changes...</span>
            )}
            {saveStatus === 'saved' && (
              <span className="text-sm text-green-500">Changes saved ✓</span>
            )}
            {saveStatus === 'error' && (
              <span className="text-sm text-red-500" title={saveError || undefined}>
                Save failed! {saveError ? '(hover for details)' : ''}
              </span>
            )}
          </div>
        </div>
        {/* Show error message if present */}
        {errorMessage && (
          <div className="error-message mb-4">
            <p>{errorMessage}</p>
          </div>
        )}

        {isLoading ? (
          <div className="loading-state">
            <div className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center gap-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <div className="text-center">
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    Processing...
                  </h3>
                  <p className="text-sm text-muted-foreground">Please wait</p>
                </div>
              </div>
            </div>
          </div>
        ) : suggestions.length === 0 ? (
          <div className="text-center p-8 bg-muted/20 rounded-lg my-8">
            <h3 className="text-foreground font-medium mb-4">No relationships found</h3>
            <p className="text-muted-foreground mb-6">No relationships were identified between the terms.</p>
            <button 
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-6 py-2 rounded-lg font-medium transition-colors" 
              onClick={() => onSubmit([])}
            >
              {stage === 'C3' ? 'Generate Subentries' : 'Continue to Next Stage'}
            </button>
          </div>
        ) : (
          <>
            {/* Three separate collapsible sections for C1 decisions */}
            {(stage === 'C2' || stage === 'C3') && (Object.keys(c1MergedTerms).length > 0 || Object.keys(c1CrossReferences).length > 0 || Object.keys(c1Subentries).length > 0) && (
              <div className="space-y-3 mb-6">
                {/* Merged Terms Section */}
                {Object.keys(c1MergedTerms).length > 0 && (
                  <div className="bg-muted/30 border border-border/50 rounded-lg">
                    <button
                      onClick={() => setIsMergedExpanded(!isMergedExpanded)}
                      className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-muted/50 transition-colors rounded-t-lg"
                    >
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        <h3 className="font-medium text-foreground">Merged Terms</h3>
                        <span className="text-sm text-foreground bg-muted/70 px-2 py-1 rounded">
                          {Object.keys(c1MergedTerms).length}
                        </span>
                      </div>
                      <svg 
                        className={`w-4 h-4 text-primary transition-transform ${isMergedExpanded ? 'rotate-180' : ''}`}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {isMergedExpanded && (
                      <div className="px-4 pb-4 border-t border-border/30">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
                          {getPaginatedItems(mergedGroups, mergedPage).map(([main, mergedList]) => (
                            <div key={main} className="text-sm bg-card rounded p-3 border border-border/30 shadow-sm">
                              <div className="font-semibold text-primary">{main}</div>
                              <div className="text-xs text-muted-foreground">merged terms:</div>
                              <ul className="mt-1 text-foreground list-disc list-inside space-y-0.5">
                                {mergedList.map((m: string) => (
                                  <li key={m}>{m}</li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                        {renderPaginationControls(
                          mergedPage, 
                          getTotalPages(Object.keys(c1MergedTerms).length), 
                          setMergedPage
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Subentries Section */}
                {Object.keys(c1Subentries).length > 0 && (
                  <div className="bg-muted/30 border border-border/50 rounded-lg">
                    <button
                      onClick={() => setIsSubentriesExpanded(!isSubentriesExpanded)}
                      className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-muted/50 transition-colors rounded-t-lg"
                    >
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                        </svg>
                        <h3 className="font-medium text-foreground">Subentries</h3>
                        <span className="text-sm text-foreground bg-muted/70 px-2 py-1 rounded">
                          {Object.keys(c1Subentries).length}
                        </span>
                      </div>
                      <svg 
                        className={`w-4 h-4 text-primary transition-transform ${isSubentriesExpanded ? 'rotate-180' : ''}`}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {isSubentriesExpanded && (
                      <div className="px-4 pb-4 border-t border-border/30">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
                          {getPaginatedItems(subentryGroups, subentriesPage).map(([parent, subs]) => (
                            <div key={parent} className="text-sm bg-card rounded p-3 border border-border/30 shadow-sm">
                              <div className="font-semibold text-primary">{parent}</div>
                                                             <div className="text-xs text-muted-foreground">subentries:</div>
                               <ul className="mt-1 text-foreground list-disc list-inside space-y-0.5">
                                 {subs.map((s: string) => (
                                   <li key={s}>{s}</li>
                                 ))}
                               </ul>
                            </div>
                          ))}
                        </div>
                        {renderPaginationControls(
                          subentriesPage, 
                          getTotalPages(Object.keys(c1Subentries).length), 
                          setSubentriesPage
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Cross-References Section */}
                {Object.keys(c1CrossReferences).length > 0 && (
                  <div className="bg-muted/30 border border-border/50 rounded-lg">
                    <button
                      onClick={() => setIsCrossRefsExpanded(!isCrossRefsExpanded)}
                      className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-muted/50 transition-colors rounded-t-lg"
                    >
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                        <h3 className="font-medium text-foreground">Cross-References</h3>
                        <span className="text-sm text-foreground bg-muted/70 px-2 py-1 rounded">
                          {Object.keys(c1CrossReferences).length}
                        </span>
                      </div>
                      <svg 
                        className={`w-4 h-4 text-primary transition-transform ${isCrossRefsExpanded ? 'rotate-180' : ''}`}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {isCrossRefsExpanded && (
                      <div className="px-4 pb-4 border-t border-border/30">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
                          {getPaginatedItems(Object.entries(c1CrossReferences), crossRefsPage).map(([term, refs]) => (
                            <div key={term} className="text-sm bg-card rounded p-3 border border-border/30 shadow-sm">
                              <span className="font-medium text-primary">{term}</span>
                              <span className="text-primary mx-2">↔</span>
                              <span className="text-muted-foreground">{refs.join(', ')}</span>
                            </div>
                          ))}
                        </div>
                        {renderPaginationControls(
                          crossRefsPage, 
                          getTotalPages(Object.keys(c1CrossReferences).length), 
                          setCrossRefsPage
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-col gap-6 mb-8 min-h-[60vh] max-h-[80vh] overflow-y-auto">
              {currentSuggestions.map((suggestion, index) => {
                const actualIndex = startIndex + index;
                const decision = decisions[actualIndex];
                
                if (!decision) return null;

                const mainTerm = decision.main_term || suggestion.suggested_main || suggestion.terms[0];
                // Include original terms from suggestion plus any additional terms from relationships
                // This handles the case where a former main term becomes a related term
                const originalTerms = new Set(suggestion.terms);
                const relationshipTerms = Object.keys(decision.relationships || {});
                // Deduplicate by creating a single Set with all terms
                const allTermsSet = new Set([...originalTerms, ...relationshipTerms]);
                const allTerms = Array.from(allTermsSet);
                const relatedTerms = allTerms.filter(term => term !== mainTerm);

                return (
                  <div key={`suggestion-${actualIndex}-${suggestion.terms.join('-')}`} className="bg-card/80 border border-border/30 rounded-lg p-5 shadow-sm backdrop-blur-sm">
                    <div className="mb-4 pb-3 border-b border-border/20">
                      <h3 className="flex items-center gap-2 m-0 text-foreground text-lg font-semibold">
                        {editedMainTerms[actualIndex] ? (
                          <input
                            type="text"
                            value={mainTerm}
                            onChange={(e) => handleMainTermChange(actualIndex, e.target.value)}
                            onBlur={() => handleMainTermEditFinished(actualIndex)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                handleMainTermEditFinished(actualIndex);
                              }
                            }}
                            className="bg-card border border-border rounded px-3 py-2 text-lg font-semibold text-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                          />
                        ) : (
                          <>
                            <span 
                              className="text-foreground cursor-pointer hover:text-primary transition-colors"
                              onClick={() => scrollToTerm(mainTerm)}
                              onMouseEnter={(e) => handleTermMouseEnter(mainTerm, e)}
                              onMouseLeave={handleTermMouseLeave}
                              onMouseMove={handleTermMouseMove}
                            >
                              {mainTerm}
                            </span>
                            <button
                              className="p-1 text-muted-foreground hover:text-foreground hover:bg-muted/30 rounded transition-all flex items-center justify-center"
                              onClick={() => toggleEditMainTerm(actualIndex)}
                              title="Edit main term"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2-2v-7"/>
                                <path d="m18.5 2.5 3 3L12 15l-4 1 1-4z"/>
                              </svg>
                            </button>
                          </>
                        )}
                        <span className="text-muted-foreground text-sm font-medium">(Group {actualIndex + 1})</span>
                      </h3>
                    </div>
                    
                    <div className="flex flex-col gap-4">
                      {relatedTerms.length > 0 && (
                        <div className="flex flex-col gap-4">
                          {relatedTerms.map((term, termIndex) => (
                            <div key={`term-${actualIndex}-${termIndex}-${term}`} className="bg-muted/20 border border-border/30 rounded-lg p-4">
                              <h4 className="m-0 mb-2 text-base font-medium text-foreground">
                                <span 
                                  className="text-foreground cursor-pointer hover:text-primary transition-colors"
                                  onClick={() => scrollToTerm(term)}
                                  onMouseEnter={(e) => handleTermMouseEnter(term, e)}
                                  onMouseLeave={handleTermMouseLeave}
                                  onMouseMove={handleTermMouseMove}
                                >
                                  {term}
                                </span>
                                {isLockedSubentry(term) && (
                                  <span className="ml-2 text-xs text-muted-foreground">(locked: subentry of {c1Subentries[term]})</span>
                                )}
                                {(stage === 'C2' || stage === 'C3') && c1Subentries[term] && (
                                  <span className="ml-2 text-xs text-muted-foreground">Subentry of {c1Subentries[term]}</span>
                                )}
                              </h4>
                              <p className="text-muted-foreground text-sm m-0 mb-3">
                                How should this term relate to "{mainTerm}"?
                              </p>
                              
                              <div className="flex flex-col gap-2">
                                <label className={`flex items-center gap-2 cursor-pointer p-3 border rounded-lg transition-all hover:bg-muted/30 ${decision.relationships?.[term] === 'merge' ? 'bg-primary/30 border-primary/60 text-primary' : 'border-border/60 bg-muted/40 text-foreground'}`}>
                                  <input
                                    type="radio"
                                    name={`relationship-${actualIndex}-${termIndex}`}
                                    value="merge"
                                    checked={decision.relationships?.[term] === 'merge'}
                                    onChange={(e) => handleRelationshipChange(actualIndex, term, 'merge')}
                                    disabled={isLockedSubentry(term)}
                                  />
                                  <span className="text-sm font-medium">Merge with main term</span>
                                </label>
                                
                                <label className={`flex items-center gap-2 cursor-pointer p-3 border rounded-lg transition-all hover:bg-muted/30 ${decision.relationships?.[term] === 'subentry' ? 'bg-primary/30 border-primary/60 text-primary' : 'border-border/60 bg-muted/40 text-foreground'}`}>
                                  <input
                                    type="radio"
                                    name={`relationship-${actualIndex}-${termIndex}`}
                                    value="subentry"
                                    checked={decision.relationships?.[term] === 'subentry'}
                                    onChange={(e) => handleRelationshipChange(actualIndex, term, 'subentry')}
                                    disabled={isLockedSubentry(term)}
                                  />
                                  <span className="text-sm font-medium">Subentry of main term</span>
                                </label>
                                
                                <label className={`flex items-center gap-2 cursor-pointer p-3 border rounded-lg transition-all hover:bg-muted/30 ${decision.relationships?.[term] === 'keep_separate' ? 'bg-primary/30 border-primary/60 text-primary' : 'border-border/60 bg-muted/40 text-foreground'}`}>
                                  <input
                                    type="radio"
                                    name={`relationship-${actualIndex}-${termIndex}`}
                                    value="keep_separate"
                                    checked={decision.relationships?.[term] === 'keep_separate'}
                                    onChange={(e) => handleRelationshipChange(actualIndex, term, 'keep_separate')}
                                  />
                                  <span className="text-sm font-medium">Keep separate</span>
                                </label>
                                
                                <label className={`flex items-center gap-2 cursor-pointer p-3 border rounded-lg transition-all hover:bg-muted/30 ${decision.relationships?.[term] === 'cross_reference' ? 'bg-primary/30 border-primary/60 text-primary' : 'border-border/60 bg-muted/40 text-foreground'}`}>
                                  <input
                                    type="radio"
                                    name={`relationship-${actualIndex}-${termIndex}`}
                                    value="cross_reference"
                                    checked={decision.relationships?.[term] === 'cross_reference'}
                                    onChange={(e) => handleRelationshipChange(actualIndex, term, 'cross_reference')}
                                    disabled={isLockedSubentry(term)}
                                  />
                                  <span className="text-sm font-medium">Cross-reference to main term</span>
                                </label>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="bg-muted/30 border-t border-border/30 px-4 py-3 rounded-b-lg" data-component="stagec-pagination">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-muted-foreground font-medium">
                    Showing {startIndex + 1}-{endIndex} of {suggestions.length} groups
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={(e) => {
                        setCurrentPage(p => Math.max(1, p - 1));
                        (e.currentTarget as HTMLElement).blur();
                      }}
                      onMouseDown={(e) => e.preventDefault()}
                      disabled={currentPage === 1}
                      className="px-2 py-1 text-xs bg-muted/50 hover:bg-muted/70 disabled:opacity-50 disabled:cursor-not-allowed rounded border border-border/30 text-foreground transition-colors"
                      data-stagec-pagination="prev"
                    >
                      ←
                    </button>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <span>Page</span>
                      <input
                        type="number"
                        tabIndex={-1}
                        min="1"
                        max={totalPages}
                        value={currentPage}
                        onChange={(e) => {
                          const value = parseInt(e.target.value);
                          if (!isNaN(value) && value >= 1 && value <= totalPages) {
                            setCurrentPage(value);
                          }
                        }}
                        className="w-8 px-1 py-1 text-center border border-border/30 rounded focus:outline-none focus:ring-1 focus:ring-purple-500 bg-background text-foreground [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        data-stagec-pagination="input"
                      />
                      <span>of {totalPages}</span>
                    </div>
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={(e) => {
                        setCurrentPage(p => Math.min(totalPages, p + 1));
                        (e.currentTarget as HTMLElement).blur();
                      }}
                      onMouseDown={(e) => e.preventDefault()}
                      disabled={currentPage === totalPages}
                      className="px-2 py-1 text-xs bg-muted/50 hover:bg-muted/70 disabled:opacity-50 disabled:cursor-not-allowed rounded border border-border/30 text-foreground transition-colors"
                      data-stagec-pagination="next"
                    >
                      →
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Enhanced Synthesis Tooltip with Scroll Support */}
      {hoveredTerm && tooltipPosition && (
        <div
          className="synthesis-tooltip"
          style={{
            position: 'fixed',
            left: tooltipPosition.x + 10,
            top: tooltipPosition.y - 10,
            zIndex: 1000,
          }}
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleTooltipMouseLeave}
        >
          {(() => {
            const synthesis = getSynthesisForTerm(hoveredTerm);
            if (!synthesis) return null;
            
            return (
              <div className="synthesis-tooltip-content">
                <div className="synthesis-tooltip-header">
                  <strong>{synthesis.term}</strong>
                  <span className="synthesis-tooltip-meta">
                    Used {synthesis.usage_count} time{synthesis.usage_count !== 1 ? 's' : ''} • Page{(synthesis.pages && synthesis.pages.includes(',')) ? 's' : ''} {synthesis.pages || '1'}
                  </span>
                </div>
                <div className="synthesis-tooltip-body">
                  {synthesis.synthesis}
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </ErrorBoundary>
  );
};

export default StageC;